import React from 'react';
import { Divider, Form, Select, Typography } from '@duxton-web/ui';
import { useParams } from 'react-router-dom';

import AllocationCalendar from './allocation-calendar';
import AxaWarningAlert from '@/features/experiment/components/axa-warning-alert';
import {
  ExperimentAssignmentStrategy,
  getAssignmentStrategy,
  getVariables,
  isViewMode,
} from '@/features/experiment/constants';
import { useExperimentContext } from '@/features/experiment/contexts/experiment-context';
import { isAxaIncompatibleSliceWindow } from '@/features/experiment/utils';

const getTimeTitleFromMinute = (minute: number) => {
  const hour = Math.floor(minute / 60);
  const minuteInHour = minute % 60;
  if (hour === 0) {
    return `${minuteInHour}m`;
  }
  if (minuteInHour === 0) {
    return `${hour}h`;
  }
  return `${hour}h${minuteInHour}m`;
};

// make an array from 15min to 1440min (24hr) with step interval of 15min
const balancedShuffleDurationOptions = Array.from(
  { length: 96 },
  (_v, k) => k * 15 + 15,
).map((duration) => ({
  label: getTimeTitleFromMinute(duration),
  value: duration * 60,
}));

const BalanceTimeSlice = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;
  const variables = getVariables(configurations);
  const experimentStrategy = getAssignmentStrategy(configurations);
  const sliceAssignments = variables?.[0]?.sliceAssignments || [];
  const durationInSeconds = variables?.[0]?.choices?.[0]?.span;

  const customTimeSliceStrategyDurationHourRemainder =
    durationInSeconds % (60 * 60);
  const blockCountEachHour =
    customTimeSliceStrategyDurationHourRemainder === 0
      ? 1
      : customTimeSliceStrategyDurationHourRemainder === 30 * 60
        ? 2
        : customTimeSliceStrategyDurationHourRemainder === 15 * 60 || 45 * 60
          ? 4
          : 1;

  const showAxaSliceWarning =
    !readOnly && isAxaIncompatibleSliceWindow(experiment?.duration);

  if (experimentStrategy !== ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE) {
    return null;
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography.Text strong>Time slice duration</Typography.Text>
        <Typography.Metadata type="secondary">
          Duration defined will be used across all treatments
        </Typography.Metadata>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Form.Item
            name="duration"
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: 'Required',
              },
            ]}
          >
            <Select
              style={{ width: 270 }}
              disabled={readOnly}
              options={balancedShuffleDurationOptions}
            />
          </Form.Item>
          <AxaWarningAlert show={showAxaSliceWarning} type="sliceWindow" />
        </div>
      </div>
      {readOnly && <Divider style={{ marginInline: 0, margin: 8 }} />}
      {readOnly && (
        <AllocationCalendar
          unixStart={experiment?.startTime?.unix()}
          unixEnd={experiment?.endTime?.unix()}
          blockCountEachHour={blockCountEachHour}
          sliceAssignments={sliceAssignments}
        />
      )}
    </>
  );
};

export default BalanceTimeSlice;
